import { getLang } from './i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

function getToken() {
  return localStorage.getItem('audiowalk_token');
}

function setToken(token) {
  if (token) {
    localStorage.setItem('audiowalk_token', token);
  } else {
    localStorage.removeItem('audiowalk_token');
  }
}

function getUser() {
  const u = localStorage.getItem('audiowalk_user');
  return u ? JSON.parse(u) : null;
}

function setUser(user) {
  if (user) {
    localStorage.setItem('audiowalk_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('audiowalk_user');
  }
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', 'X-Lang': getLang(), ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || 'Ошибка сервера');
  }

  return data;
}

// --- Auth ---
export async function register(name, email, password, phone, city) {
  const data = await request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, password, phone: phone || '', city: city || '' }),
  });
  setToken(data.token);
  setUser(data.user);
  return data;
}

export async function login(email, password) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(data.token);
  setUser(data.user);
  return data;
}

export async function logout() {
  setToken(null);
  setUser(null);
}

// Public endpoints — no auth needed
export async function publicGetLanguages() {
  const res = await fetch(`${API_URL}/public/languages`);
  return res.json();
}

export async function publicGetCities() {
  const res = await fetch(`${API_URL}/public/cities?lang=${getLang()}`);
  if (!res.ok) throw new Error('Ошибка');
  return res.json();
}
export async function publicGetCity(slug) {
  const res = await fetch(`${API_URL}/public/city/${slug}?lang=${getLang()}`);
  if (!res.ok) throw new Error('Город не найден');
  return res.json();
}
export async function publicGetExcursions() {
  const res = await fetch(`${API_URL}/public/excursions?lang=${getLang()}`);
  if (!res.ok) throw new Error('Ошибка');
  return res.json();
}
export async function publicGetPreview(excursionId) {
  const res = await fetch(`${API_URL}/public/excursion/${excursionId}/preview?lang=${getLang()}`);
  if (!res.ok) throw new Error('Не найдено');
  return res.json();
}

export async function resetPassword(email) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function getMe() {
  return request('/auth/me');
}

// --- Cities ---
export async function getCities() {
  return request('/cities');
}

// --- Excursions ---
export async function getExcursionsByCity(cityId) {
  return request(`/excursions/city/${cityId}`);
}

export async function getExcursion(id) {
  return request(`/excursions/${id}`);
}

export async function getSteps(excursionId) {
  return request(`/excursions/${excursionId}/steps`);
}

export async function applyPromoCode(excursionId, code) {
  return request(`/excursions/${excursionId}/apply-code`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function payForExcursion(excursionId) {
  return request(`/excursions/${excursionId}/pay`, { method: 'POST' });
}

export async function repurchaseExcursion(excursionId) {
  return request(`/excursions/${excursionId}/repurchase`, { method: 'POST' });
}

export async function checkDiscountCode(excursionId, codeWord) {
  return request(`/excursions/${excursionId}/check-discount`, {
    method: 'POST',
    body: JSON.stringify({ code_word: codeWord }),
  });
}

export async function payWithDiscount(excursionId, codeId) {
  return request(`/excursions/${excursionId}/pay-with-discount`, {
    method: 'POST',
    body: JSON.stringify({ code_id: codeId }),
  });
}

export async function updateProgress(excursionId, currentStep, isCompleted) {
  return request(`/excursions/${excursionId}/progress`, {
    method: 'POST',
    body: JSON.stringify({ current_step: currentStep, is_completed: isCompleted }),
  });
}

// --- Offline answer queue ---
// If there is no internet while the user plays a downloaded quest, answers are
// stored on the device and automatically re-sent once the connection is back.
const PENDING_ANSWERS_KEY = 'audiowalk_pending_answers';

function getPendingAnswers() {
  try { return JSON.parse(localStorage.getItem(PENDING_ANSWERS_KEY)) || []; }
  catch { return []; }
}
function setPendingAnswers(arr) {
  try { localStorage.setItem(PENDING_ANSWERS_KEY, JSON.stringify(arr.slice(-500))); }
  catch { /* storage full — ignore */ }
}

export async function logAnswer(excursionId, data) {
  try {
    const r = await request(`/excursions/${excursionId}/answer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Connection is alive — good moment to drain any backlog
    flushPendingAnswers();
    return r;
  } catch (e) {
    // Could not send (most likely offline) — keep it for later
    const q = getPendingAnswers();
    q.push({ excursionId, data, ts: Date.now() });
    setPendingAnswers(q);
    return null;
  }
}

let _flushing = false;
export async function flushPendingAnswers() {
  if (_flushing) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const q = getPendingAnswers();
  if (q.length === 0) return;
  _flushing = true;
  try {
    const keep = [];
    for (const item of q) {
      try {
        await request(`/excursions/${item.excursionId}/answer`, {
          method: 'POST',
          body: JSON.stringify(item.data),
        });
        // sent successfully — do not keep
      } catch (e) {
        // Keep it to retry next time (offline again, or temporary error)
        keep.push(item);
      }
    }
    setPendingAnswers(keep);
  } finally {
    _flushing = false;
  }
}

// Auto-flush: on reconnect and shortly after app start
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flushPendingAnswers(); });
  setTimeout(() => { flushPendingAnswers(); }, 4000);
}

export async function tochkaCreatePayment(excursionId) {
  return request(`/excursions/${excursionId}/pay-create`, { method: 'POST' });
}
export async function tochkaVerifyPayment(excursionId, paymentLinkId) {
  return request(`/excursions/${excursionId}/pay-verify`, {
    method: 'POST',
    body: JSON.stringify({ paymentLinkId }),
  });
}

export async function submitReview(excursionId, rating, comment) {
  return request(`/excursions/${excursionId}/review`, {
    method: 'POST',
    body: JSON.stringify({ rating, comment }),
  });
}

// --- Profile ---
export async function getMyExcursions() {
  return request('/profile/my-excursions');
}

// --- Admin ---
export async function adminGetUsers() {
  return request('/admin/users');
}
export async function adminExportUsers(from, to) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const url = `${API_URL}/admin/users/export?${params.toString()}`;
  const token = getToken();
  const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!resp.ok) {
    let msg = 'Ошибка экспорта';
    try { const j = await resp.json(); msg = j.error || msg; } catch (e) {}
    throw new Error(msg);
  }
  const blob = await resp.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `users_${from || 'all'}_${to || 'all'}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}
export async function adminGetUserDetail(id) {
  return request(`/admin/users/${id}`);
}
export async function adminDeleteUserExcursion(userId, excursionId) {
  return request(`/admin/users/${userId}/excursion`, {
    method: 'DELETE',
    body: JSON.stringify({ excursion_id: excursionId }),
  });
}
export async function adminDeleteUser(userId) {
  return request(`/admin/users/${userId}`, { method: 'DELETE' });
}
export async function adminGetExcursionPlayers(excId) {
  return request(`/admin/excursions/${excId}/players`);
}
export async function adminDeleteUserAnswers(userId, excursionId, attemptId) {
  return request(`/admin/users/${userId}/answers`, {
    method: 'DELETE',
    body: JSON.stringify({ excursion_id: excursionId, attempt_id: attemptId }),
  });
}

export async function adminResolveMapLink(url) {
  return request(`/admin/resolve-map-link?url=${encodeURIComponent(url)}`);
}

export async function adminGetLanguages() {
  return request('/admin/languages');
}
export async function adminCreateLanguage(data) {
  return request('/admin/languages', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminUpdateLanguage(code, data) {
  return request(`/admin/languages/${code}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminDeleteLanguage(code) {
  return request(`/admin/languages/${code}`, { method: 'DELETE' });
}

export async function adminGetCities() {
  return request('/admin/cities');
}
export async function adminCreateCity(data) {
  return request('/admin/cities', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminUpdateCity(id, data) {
  return request(`/admin/cities/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminDeleteCity(id) {
  return request(`/admin/cities/${id}`, { method: 'DELETE' });
}

export async function adminGetExcursions() {
  return request('/admin/excursions');
}
export async function adminCreateExcursion(data) {
  return request('/admin/excursions', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminUpdateExcursion(id, data) {
  return request(`/admin/excursions/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminDeleteExcursion(id) {
  return request(`/admin/excursions/${id}`, { method: 'DELETE' });
}

export async function adminGetSteps(excursionId) {
  return request(`/admin/excursions/${excursionId}/steps`);
}
export async function adminCreateStep(excursionId, data) {
  return request(`/admin/excursions/${excursionId}/steps`, { method: 'POST', body: JSON.stringify(data) });
}
export async function adminUpdateStep(id, data) {
  return request(`/admin/steps/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function adminDeleteStep(id) {
  return request(`/admin/steps/${id}`, { method: 'DELETE' });
}

export async function adminGetPromoCodes() {
  return request('/admin/promo-codes');
}
export async function adminDeletePromoCodes(ids) {
  return request('/admin/promo-codes/delete', { method: 'POST', body: JSON.stringify({ ids }) });
}
export async function adminGeneratePromoCodes(excursionId, count, prefix) {
  return request('/admin/promo-codes/generate', {
    method: 'POST',
    body: JSON.stringify({ excursion_id: excursionId, count, prefix }),
  });
}

export async function adminGetDiscountCodes() {
  return request('/admin/discount-codes');
}
export async function adminCreateDiscountCode(data) {
  return request('/admin/discount-codes', { method: 'POST', body: JSON.stringify(data) });
}
export async function adminDeleteDiscountCode(id) {
  return request(`/admin/discount-codes/${id}`, { method: 'DELETE' });
}

export async function adminUploadFile(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/admin/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  return res.json();
}

export { getToken, getUser, setUser };
